/** @prettier */

/**
 * 支払いユーザ譲渡の認証ダイアログ
 */

import {useState} from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {useTranslation} from 'react-i18next';

//MTH component import
import APIErrorMessage from '../../component/APIErrorMessage';
//API import
import {
  deleteBillingAuthorizationTransfer,
  postBillingAuthorizationTransferAccept,
  getCompositionsGateway,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';
import {useParams} from 'react-router-dom';

import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';

/**
 * 権限譲渡の同意確認ダイアログ
 * @param gwID ゲートウェイID
 * @param setGWInfo ゲートウェイ情報更新関数
 * @param isOpenDialog ダイアログオープン
 * @param setIsOpenDialog  ダイアログオープン更新関数
 * @param setMessage 確認メッセージ更新関数
 * @param setIsTransferMessageDialog 確認メッセージダイアログ更新関数
 * @returns JSX
 */
const TransferConsentDialog = (props: {
  gwID: string;
  setGWInfo: (arg0: apiIF.res.getCompositionsGateway) => void;
  isOpenDialog: boolean;
  setIsOpenDialog: (arg0: boolean) => void;
  setMessage: React.Dispatch<React.SetStateAction<string>>;
  setIsTransferMessageDialog: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [isOpenAPIErrorDialog, setIsOpenAPIErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWaitAccept, setAPIWaitAccept] = useState(false); //API応答待ちフラグ(Accept)
  const [apiWaitReject, setAPIWaitReject] = useState(false); //API応答待ちフラグ(Reject)

  const trans = useTranslation().t; //翻訳関数
  const {siteID} = useParams();

  /**
   * ACCEPTボタン押下時処理
   */
  const acceptTransfer = async () => {
    try {
      //パスパラメータを取得できない場合
      if (siteID === undefined || props.gwID === undefined) return;
      setAPIWaitAccept(true);
      // 支払いユーザの譲渡を受け入れる
      await postBillingAuthorizationTransferAccept(siteID, props.gwID);
      // ゲートウェイ情報の更新
      const gwInfos = await getCompositionsGateway(siteID, {'max-result': 10});
      props.setGWInfo(gwInfos);
      // ダイアログを閉じる
      props.setIsOpenDialog(false);
      props.setMessage(
        `${trans('AuthorizationTransferDialog.message.accept')}`
      );
      props.setIsTransferMessageDialog(true);
    } catch (err: any) {
      //譲渡ユーザーの所在国が未設定場合
      //フロントでは、ダイアログを表示する時点でこのエラーを判断しているため、通常動作でこのメッセージが表示されることはない
      if (err.response.data.code === 'COUNTRY_NOT_SELECTED') {
        alert(trans('TransferConsentDialog.alertMsg.countryNotSelected'));
      }
      //支払いユーザの所在国と譲渡ユーザーの所在国が一致しない場合
      else if (err.response.data.code === 'COUNTRY_NOT_MATCH') {
        alert(trans('TransferConsentDialog.alertMsg.countryNotMatch'));
      } else {
        setIsOpenAPIErrorDialog(true);
      }
    } finally {
      setAPIWaitAccept(false);
    }
  };

  /**
   * REJECTボタン押下時処理
   */
  const rejectTransfer = async () => {
    try {
      //パスパラメータを取得できない場合
      if (siteID === undefined || props.gwID === undefined) return;
      setAPIWaitReject(true);
      // 支払いユーザの譲渡を拒否する
      await deleteBillingAuthorizationTransfer(siteID, props.gwID);
      // ゲートウェイ情報の更新
      const gwInfos = await getCompositionsGateway(siteID, {'max-result': 10});
      props.setGWInfo(gwInfos);
      // ダイアログを閉じる
      props.setIsOpenDialog(false);
      props.setMessage(
        `${trans('AuthorizationTransferDialog.message.decline')}`
      );
      props.setIsTransferMessageDialog(true);
    } catch {
      setIsOpenAPIErrorDialog(true);
    } finally {
      setAPIWaitReject(false);
    }
  };

  /**
   * ダイアログ終了時処理
   */
  const handleClose = () => {
    props.setIsOpenDialog(false);
  };

  //パスパラメータを取得できない場合
  if (siteID === undefined || props.gwID === null) return <></>;

  return (
    <>
      <Dialog open={props.isOpenDialog} onClose={handleClose}>
        <DialogTitle>{trans('TransferConsentDialog.title')}</DialogTitle>
        <DialogContent>
          {/* ダイアログに表示するメッセージ */}
          <DialogContentText>
            {trans('TransferConsentDialog.msg')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {/* REJECTボタンの設定 */}
          <SecondaryButton
            onClick={() => rejectTransfer()}
            buttonText={trans('TransferConsentDialog.buttonTxt.reject')}
            apiWait={apiWaitReject}
          />
          {/* ACCEPTボタンの設定 */}
          <PrimaryButton
            onClick={() => acceptTransfer()}
            buttonText={trans('TransferConsentDialog.buttonTxt.accept')}
            apiWait={apiWaitAccept}
          />
        </DialogActions>
      </Dialog>
      {/* エラー表示 */}
      <APIErrorMessage
        open={isOpenAPIErrorDialog}
        setOpen={setIsOpenAPIErrorDialog}
        id={`EditErrDlgBtn`}
      />
    </>
  );
};
export default TransferConsentDialog;
